import React, { createContext, ReactNode, useContext } from 'react';
import { useIdentity } from '../hooks/useIdentity';

interface FlikHttpAuthOptions {
  headerName: string;
  token: {
    accessTokenKey: string;
    refreshTokenKey: string;
  };
  skiplist?: {
    paths?: string[];
  };
  storage?: {
    get: (key: string) => string | null;
    set: (key: string, value: string) => void;
    remove: (key: string) => void;
  };
}

interface IdentityContextType {
  isLoggedIn: boolean;
  isLoading: boolean;
  error: string | null;
  signInState: {
    email: string;
    verificationCode: string;
    step: 'email' | 'verification' | 'complete';
    isLoading: boolean;
    error: string | null;
    successMessage: string | null;
  };
  signIn: (email: string) => Promise<void>;
  verifyCode: (verificationCode: string) => Promise<void>;
  resendCode: () => Promise<void>;
  signOut: () => Promise<void>;
  resetSignIn: () => void;
  setSignInState: React.Dispatch<
    React.SetStateAction<{
      email: string;
      verificationCode: string;
      step: 'email' | 'verification' | 'complete';
      isLoading: boolean;
      error: string | null;
      successMessage: string | null;
    }>
  >;
  auth: any; // Expose the auth object for session management
  // Organization management
  currentOrganization: string | null;
  availableOrganizations: Array<{
    organization_id: string;
    organization_name?: string;
    accessToken: string;
    refreshToken: string;
    lastUsed?: number;
  }>;
  switchOrganization: (organizationId: string) => void;
  addOrganizationAuth: (organizationId: string, authData: any) => void;
  removeOrganization: (organizationId: string) => void;
}

const IdentityContext = createContext<IdentityContextType | undefined>(
  undefined,
);

interface IdentityProviderProps {
  children: ReactNode;
  authOptions?: FlikHttpAuthOptions;
}

export const IdentityProvider: React.FC<IdentityProviderProps> = ({
  children,
  authOptions,
}) => {
  try {
    const identity = useIdentity(authOptions);

    return (
      <IdentityContext.Provider value={identity}>
        {children}
      </IdentityContext.Provider>
    );
  } catch (error) {
    console.error('IdentityProvider - Error initializing identity:', error);
    throw error;
  }
};

export const useIdentityContext = (): IdentityContextType => {
  const context = useContext(IdentityContext);
  if (context === undefined) {
    throw new Error(
      'useIdentityContext must be used within an IdentityProvider',
    );
  }
  return context;
};
